<template>
  <div>
    <label>
      {{ $t('form.customer_card') }}
    </label>
    <b-card>
      <loader v-if="loading" />

      <div v-show="!createCard && !editCardData">
        <div
          class="row  justify-content-between mb-3 credit-card-body"
          v-for="(card, index) in cards"
          :key="index"
        >
          <p class="col-8 mb-0">
            <i :class="`fab fa-cc-${normalizeClassName(card.card_brand)} fa-lg text-muted`" />
            {{ `**** **** **** ${card.card_last4}` }}
          </p>

          <div class="col-4 delete-card">
            <p v-if="card.default" class="text-success mb-0">
              {{ $t('global.default') }}
            </p>

            <b-button
              class="p-0"
              size="sm"
              variant="link"
              @click="removeCard"
            >
              <u>{{ $t('button.delete') }}</u>
            </b-button>
          </div>
        </div>

        <p v-if="cards.length === 0" class="text-muted w-100 text-center">
          {{ $t('billing.youDoNotHaveCardsYet') }}
        </p>

        <b-button
          class="mt-2 w-100 billing-add-card"
          variant="outline-primary"
          @click="showStripeForm(true)"
        >
          <p class="mb-0 py-1 px-4">
            + {{ $t('billing.addNewCard') }}
          </p>
        </b-button>
      </div>

      <b-form v-if="createCard">
        <div id="card-element">
          <label> {{ $t('billing.cardDetails') }} <span class="text-danger">
            {{ $t('form.required_star') }}
          </span></label>

          <div
            style="min-height: 40px;"
            class="mb-4"
          >
            <!-- A Stripe Element will be inserted here. -->
            <StripeElementCard
              ref="card-form"
              :pk="stripePublicKey"
              hide-postal-code
              @token="onTokenCreated"
              @error="onStripeError"
            />
          </div>

          <div class="d-flex justify-content-between">
            <b-btn @click.prevent="cancelCardAdd" variant="outline-primary">
              {{ $t('button.cancel') }}
            </b-btn>
            <b-btn @click.prevent="addCard">
              {{ $t('button.add') }}
            </b-btn>
          </div>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { StripeElementCard } from '@vue-stripe/vue-stripe';

export default {
  name: 'CardForm',
  components: {
    StripeElementCard,
  },
  props: {
    cards: {
      type: Array,
      default: () => []
    },
    mode: {
      type: String,
      default: 'test'
    },
    cardLoading: {
      type: Boolean,
      default: false
    },
    customerEmail: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      email: '',
      emailTouched: false,
      loading: false,
      createCard: false,
      public_key: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      public_test_key: process.env.VUE_APP_STRIPE_PUBLISHABLE_TEST_KEY,
      editCardData: null
    }
  },
  computed: {
    emailState() {
      if (!this.emailTouched) {
        return null;
      }

      return this.emailTouched && !this.invalidFeedback;
    },
    invalidFeedback() {
      if (!this.email.length) {
        return 'Please fill email';
      } else if (!this.email.includes('@') || !this.email.includes('.')) {
        return 'Invalid email';
      }

      return '';
    },
    stripePublicKey() {
      return this.mode === 'live' ? this.public_key : this.public_test_key
    }
  },
  created() {
    this.assembleEmail(this.customerEmail);
    this.$bus.$on('stripe-card-add', (mode) => {
      if (this.mode !== mode) {
        this.createCard = false;
      }
    });
  },
  methods: {
    normalizeClassName: (string) => {
      return string ? string.toLowerCase().replace(' ', '') : '';
    },
    showStripeForm(show) {
      this.$bus.$emit('stripe-card-add', this.mode);
      this.createCard = show;
    },
    addCardByToken(tokenId, email) {
      const params = {
        token: tokenId,
        customer_email: email
      }

      return this.putData(`resources/${this.$store.getters['resource/currentResource'].key}/pricing/payment-data/stripe/token/${this.mode}`, params, false)
          .then(({ data }) => {
            this.$emit('card-add', {
              card_brand: data.card_brand,
              card_last4: data.card_last4,
              customer_email: data.customer_email,
              expiration_date: data.expiration_date,
              id: data.id,
              livemode: data.livemode,
              stripe_card_id: data.stripe_card_id,
              stripe_customer_id: data.stripe_customer_id,
              stripe_payment_method_billing_details: data.stripe_payment_method_billing_details,
              stripe_payment_method_id: data.stripe_payment_method_id,
              resource_id: data.resource_id
            });
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          }).finally(() => {
            this.cancelCardAdd();
            this.loading = false;
          });
    },
    onTokenCreated(token) {
      if (token) {
        this.addCardByToken(token.id, this.email);
      } else {
        this.$noty.error('Token not found!');
        this.loading = false;
      }
    },
    onStripeError(error) {
      console.log('STRIPE ERROR:', error);
      this.loading = false;
    },
    cancelCardAdd() {
      this.$refs['card-form'].clear();
      this.showStripeForm();
    },
    addCard() {
      this.$refs['card-form'].submit();
      this.loading = true;
    },
    removeCard() {
      this.loading = true;

      return this.deleteData(
          `resources/${this.$store.getters['resource/currentResource'].key}/pricing/payment-data/stripe/${this.mode}`,
          null,
          false
      ).then(({data}) => {
        if (data.deleted) {
          this.$emit('card-remove', this.mode);
        }
      }).catch((error) => {
        if (error && error.response) {
          this.$noty.error(error.response.data.error_description);
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    assembleEmail(email) {
      this.email = email;
      if (email) {
        this.emailTouched = true;
      }
    }

  },
  watch: {
    cardLoading: {
      handler: function (value) {
        this.loading = value;
      },
      immediate: true
    },
    customerEmail(value) {
      this.assembleEmail(value);
    }
  },
  destroyed() {
    this.$bus.$off('stripe-card-add');
  }
}
</script>